import { useState } from 'react';
import { X, Upload } from 'lucide-react';
import { Button } from '../ui/button';
import { supabase } from '../../integrations/supabase/client';
import { useToast } from '../../hooks/use-toast';

interface AnexoUploadProps {
  isOpen: boolean;
  onClose: () => void;
  processoId: string;
  onUploadComplete: () => void;
}

export default function AnexoUpload({ isOpen, onClose, processoId, onUploadComplete }: AnexoUploadProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [tipoDocumento, setTipoDocumento] = useState('');

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!file) {
      toast({
        title: 'Erro',
        description: 'Por favor, selecione um arquivo',
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);

    try {
      // Upload do arquivo para o storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${processoId}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('anexos-processo')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Obter URL pública do arquivo
      const { data: { publicUrl } } = supabase.storage
        .from('anexos-processo')
        .getPublicUrl(fileName);

      // Salvar metadados no banco
      const { error: dbError } = await supabase
        .from('anexos_processo')
        .insert({
          processo_id: processoId,
          nome_arquivo: fileName,
          tipo: tipoDocumento,
          tipo_mime: file.type,
          tamanho_bytes: file.size,
          url_arquivo: publicUrl,
          uploaded_by: (await supabase.auth.getUser()).data.user?.id
        });

      if (dbError) throw dbError;

      toast({
        title: 'Anexo adicionado',
        description: 'O arquivo foi enviado com sucesso.'
      });

      onUploadComplete();
      onClose();
      setFile(null);
      setTipoDocumento('');
    } catch (error: any) {
      console.error('Erro ao enviar anexo:', error);
      toast({
        title: 'Erro ao enviar',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between rounded-t-lg">
          <h2 className="text-xl font-semibold">Adicionar Anexo</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label htmlFor="tipo" className="block text-sm font-medium text-gray-700 mb-1">
              Tipo de Documento *
            </label>
            <select
              id="tipo"
              required
              value={tipoDocumento}
              onChange={(e) => setTipoDocumento(e.target.value)}
              className="w-full mt-1 rounded-md border border-gray-300 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="">Selecione o tipo</option>
              <option value="contrato_assinado">Contrato Assinado</option>
              <option value="documento_parte">Documento da Parte</option>
              <option value="comprovante">Comprovante</option>
              <option value="outro">Outro</option>
            </select>
          </div>

          <div>
            <label htmlFor="arquivo" className="block text-sm font-medium text-gray-700 mb-1">
              Arquivo *
            </label>
            <div className="mt-1">
              <input
                id="arquivo"
                type="file"
                required
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-orange-50 file:text-orange-700 hover:file:bg-orange-100"
              />
              {file && (
                <p className="text-sm text-gray-600 mt-2">
                  Arquivo selecionado: {file.name} ({(file.size / 1024).toFixed(2)} KB)
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={loading}>
              <Upload className="mr-2 h-4 w-4" />
              {loading ? 'Enviando...' : 'Enviar'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
